import React, {
  useState,
  useEffect,
  useRef,
  useCallback
} from "react";

import axios from "axios";
import classNames from "classnames";

import {
  Typography,
  Button,
  IconButton,
  Grid,
  Box,
  TextField,
  Modal
} from "@material-ui/core";

import MUIDataTable from "mui-datatables";
import { MuiThemeProvider } from "@material-ui/core/styles";

import Autocomplete from "@mui/material/Autocomplete";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";

import ClearIcon from "@material-ui/icons/Clear";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@material-ui/core/CircularProgress";

import Skeleton from "@material-ui/lab/Skeleton";

import {
  API_BASE_URL,
  token,
  userLevel,
  adminId
} from "../../../../constants";

import { customTheme } from "../../../StyleComponents/Styledatatable";
import useStyles from "../../../StyleComponents/StyleFunctionComponent";


const MuiAlert = React.forwardRef(function MuiAlert(props, ref) {
  return (
    <Alert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});


const FinanceEnquiries = () => {

  const classes = useStyles();


  // ============================================================
  // STATES
  // ============================================================

  const [data, setData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const loadingRequestsRef = useRef(0);


  const startLoading = useCallback(() => {

    loadingRequestsRef.current += 1;

    setIsLoading(true);

  }, []);


  const stopLoading = useCallback(() => {

    loadingRequestsRef.current = Math.max(
      loadingRequestsRef.current - 1,
      0
    );

    if (loadingRequestsRef.current === 0) {
      setIsLoading(false);
    }

  }, []);


  // ============================================================
  // PAGINATION
  // ============================================================

  const [perPage, setPerPage] = useState(50);

  const [page, setPage] = useState(0);

  const [totalCount, setTotalCount] = useState(0);


  // ============================================================
  // DATE
  // ============================================================

  const currentDate = new Date();

  const eightDaysAgo = new Date(currentDate);

  eightDaysAgo.setDate(
    eightDaysAgo.getDate() - 8
  );


  const defaultFromDate = eightDaysAgo
    .toISOString()
    .split("T")[0];


  const defaultToDate = currentDate
    .toISOString()
    .split("T")[0];


  const [fromDate, setFromDate] =
    useState(defaultFromDate);


  const [toDate, setToDate] =
    useState(defaultToDate);


  // ============================================================
  // STATE FILTER
  // ============================================================

  const [selectedState, setSelectedState] =
    useState(null);

  const [stateOptions, setStateOptions] =
    useState([]);


  // ============================================================
  // CLIENT / MOVE TYPE / ENQUIRY TYPE
  // ============================================================

  const [clientName, setClientName] =
    useState("");

  const [moveType, setMoveType] =
    useState("");

  const [enquiryType, setEnquiryType] =
    useState("");


  // ============================================================
  // SELECTED ROWS
  // Stores TABLE INDEXES
  // Example: [0, 2, 5]
  // ============================================================

  const [selectedRows, setSelectedRows] =
    useState([]);


  // ============================================================
  // DATA LOADED
  // ============================================================

  const [isDataLoaded, setIsDataLoaded] =
    useState(false);

  const [
    enableMoveAfterFetch,
    setEnableMoveAfterFetch
  ] = useState(false);


  // ============================================================
  // SNACKBAR
  // ============================================================

  const [snackbarOpen, setSnackbarOpen] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState("");

  const [snackbarSeverity, setSnackbarSeverity] =
    useState("success");


  // ============================================================
  // CONFIRM MODAL
  // ============================================================

  const [
    openConfirmModal,
    setOpenConfirmModal
  ] = useState(false);


  // ============================================================
  // VIEW MODAL
  // ============================================================

  const [
    openViewModal,
    setOpenViewModal
  ] = useState(false);

  const [
    viewRowData,
    setViewRowData
  ] = useState(null);


  // ============================================================
  // APPLIED LIST FILTERS
  // ============================================================

  const [appliedFilters, setAppliedFilters] =
    useState({

      stateId: null,

      fromDate:
        defaultFromDate,

      toDate:
        defaultToDate,

      enquiryType: ""

    });


  // ============================================================
  // STATE LIST
  // ============================================================

  useEffect(() => {

    const fetchStateList =
      async () => {

        startLoading();

        try {

          const config = {

            headers: {

              "Content-Type":
                "application/json",

              Accept:
                "application/json",

              Authorization:
                `${token}`

            },

            timeout: Infinity

          };


          const response =
            await axios.post(

              `${API_BASE_URL}/api/admin/v2/state-list`,

              {},

              config

            );


          const stateList =
            response?.data?.data?.map(
              (item) => ({

                id:
                  item.state_id,

                title:
                  item.state_name

              })
            ) || [];


          setStateOptions(
            stateList
          );


        } catch (error) {

          console.error(
            "Error fetching state:",
            error
          );

        } finally {

          stopLoading();

        }

      };


    fetchStateList();

  }, [
    startLoading,
    stopLoading
  ]);


  // ============================================================
  // STATE CHANGE
  // ============================================================

  const handleStateChange = (
    event,
    newValue
  ) => {

    setSelectedState(
      newValue
    );

    setIsDataLoaded(false);

    setSelectedRows([]);

  };


  // ============================================================
  // DATE CHANGE
  // ============================================================

  const handleDateChange =
    (e) => {

      const {
        name,
        value
      } = e.target;


      if (
        name === "fromDate"
      ) {

        setFromDate(
          value
        );

      }


      if (
        name === "toDate"
      ) {

        setToDate(
          value
        );

      }


      setIsDataLoaded(false);

      setSelectedRows([]);

    };


  // ============================================================
  // FINANCE ENQUIRY LIST
  // ============================================================

  const fetchData =
    async () => {

      startLoading();


      try {

        const config = {

          headers: {

            "Content-Type":
              "application/json",

            Accept:
              "application/json",

            Authorization:
              token,

            adminId:
              adminId,

            userLevel:
              userLevel

          }

        };


        const payload = {

          start_date:
            appliedFilters.fromDate,

          end_date:
            appliedFilters.toDate,

          state_id:
            appliedFilters.stateId ||
            null,

          enquiry_type:
            appliedFilters.enquiryType ||
            null,

          pagination:
            perPage,

          page:
            page + 1

        };


        console.log(
          "Finance List Payload:",
          payload
        );


        const response =
          await axios.post(

            `${API_BASE_URL}/api/admin/v2/finance-loan-enquiries-lists`,

            payload,

            config

          );


        console.log(
          "Finance List Response:",
          response.data
        );


        const financeData =
          response?.data?.data;


        const rows =
          financeData
            ?.finance_loan_enquiries
            ?.map(
              (item, index) => ({

                id:
                  item.id,

                serial:
                  page *
                    perPage +
                  index +
                  1,

                enquiry_name:
                  item.enquiry_name ||
                  "",

                enquiry_type:
                  item.enquiry_type ||
                  "",

                product_code:
                  item.product_code ||
                  "",

                response_data:
                  item.response_data ||
                  "",

                user:
                  item.user ||
                  "",

                mobile:
                  item.mobile ||
                  "",

                email:
                  item.email ||
                  "",

                state:
                  item.state ||
                  "",

                city:
                  item.city ||
                  "",

                taluka:
                  item.taluka ||
                  "",

                created_at:
                  item.created_at ||
                  ""

              })
            ) || [];


        setData(
          rows
        );


        setTotalCount(
          Number(
            financeData?.total ||
            0
          )
        );


        setSelectedRows([]);


        if (
          enableMoveAfterFetch
        ) {

          setIsDataLoaded(
            true
          );


          setEnableMoveAfterFetch(
            false
          );


          setSnackbarSeverity(
            "success"
          );


          setSnackbarMessage(
            "Finance enquiries loaded successfully"
          );


          setSnackbarOpen(
            true
          );

        }


      } catch (error) {

        console.error(
          "Finance enquiry listing error:",
          error
        );


        setData([]);

        setTotalCount(0);

        setIsDataLoaded(false);

        setEnableMoveAfterFetch(false);


        setSnackbarSeverity(
          "error"
        );


        setSnackbarMessage(

          error?.response
            ?.data?.message ||
          "Failed to fetch finance enquiries"

        );


        setSnackbarOpen(
          true
        );


      } finally {

        stopLoading();

      }

    };


  // ============================================================
  // LIST API CALL
  // ============================================================

  useEffect(() => {

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    perPage,
    appliedFilters
  ]);


  // ============================================================
  // GET DATA
  // ============================================================

  const handleDataLoaded = () => {

    if (!fromDate) {

      setSnackbarSeverity(
        "error"
      );

      setSnackbarMessage(
        "Start Date is required"
      );

      setSnackbarOpen(
        true
      );

      return;

    }


    if (!toDate) {

      setSnackbarSeverity(
        "error"
      );

      setSnackbarMessage(
        "End Date is required"
      );

      setSnackbarOpen(
        true
      );

      return;

    }


    if (
      new Date(fromDate) >
      new Date(toDate)
    ) {

      setSnackbarSeverity(
        "error"
      );

      setSnackbarMessage(
        "Start Date cannot be greater than End Date"
      );

      setSnackbarOpen(
        true
      );

      return;

    }


    setPage(0);

    setSelectedRows([]);

    setIsDataLoaded(false);

    setEnableMoveAfterFetch(true);


    setAppliedFilters({

      stateId:
        selectedState?.id ||
        null,

      fromDate:
        fromDate,

      toDate:
        toDate,

      enquiryType:
        enquiryType ||
        ""

    });

  };


  // ============================================================
  // RESET
  // ============================================================

  const handleResetFields = () => {

    setSelectedState(
      null
    );

    setClientName("");

    setMoveType("");

    setEnquiryType("");

    setFromDate(
      defaultFromDate
    );

    setToDate(
      defaultToDate
    );

    setSelectedRows([]);

    setPage(0);

    setIsDataLoaded(false);

    setEnableMoveAfterFetch(false);


    setAppliedFilters({

      stateId:
        null,

      fromDate:
        defaultFromDate,

      toDate:
        defaultToDate,

      enquiryType:
        ""

    });


    setSnackbarSeverity(
      "success"
    );


    setSnackbarMessage(
      "Form fields reset successfully"
    );


    setSnackbarOpen(
      true
    );

  };


  // ============================================================
  // SINGLE ROW SELECT
  // ============================================================

  const handleRowSelect = (
    rowIndex
  ) => {

    setSelectedRows(
      (previous) => {

        if (
          previous.includes(
            rowIndex
          )
        ) {

          return previous.filter(
            (row) =>
              row !== rowIndex
          );

        }


        return [
          ...previous,
          rowIndex
        ];

      }
    );

  };


  // ============================================================
  // SELECT ALL CURRENT PAGE
  // ============================================================

  const handleSelectAllCheck =
    () => {

      if (
        selectedRows.length ===
          data.length &&
        data.length !== 0
      ) {

        setSelectedRows([]);

      } else {

        setSelectedRows(

          Array.from(

            {
              length:
                data.length
            },

            (_, index) =>
              index

          )

        );

      }

    };


  // ============================================================
  // MOVE VALIDATION
  // ============================================================

  const validateMoveEnquiryForm =
    () => {

      if (!fromDate) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "Start Date is required"
        );

        setSnackbarOpen(
          true
        );

        return false;

      }


      if (!toDate) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "End Date is required"
        );

        setSnackbarOpen(
          true
        );

        return false;

      }


      if (
        new Date(fromDate) >
        new Date(toDate)
      ) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "Start Date cannot be greater than End Date"
        );

        setSnackbarOpen(
          true
        );

        return false;

      }


      if (!selectedState) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "State is required"
        );

        setSnackbarOpen(
          true
        );

        return false;

      }


      if (!clientName) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "Client Name is required"
        );

        setSnackbarOpen(
          true
        );

        return false;

      }


      if (!moveType) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "Move Type is required"
        );

        setSnackbarOpen(
          true
        );

        return false;

      }


      if (
        selectedRows.length ===
        0
      ) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "Please select at least one enquiry"
        );

        setSnackbarOpen(
          true
        );

        return false;

      }


      return true;

    };


  // ============================================================
  // MOVE BUTTON
  // ============================================================

  const handleMoveEnquiryClick =
    () => {

      if (
        !validateMoveEnquiryForm()
      ) {

        return;

      }


      setOpenConfirmModal(
        true
      );

    };


  // ============================================================
  // MOVE FINANCE ENQUIRIES
  // MULTIPLE IDs FIXED
  // ============================================================

  const handleSubmits =
    async () => {

      if (
        !validateMoveEnquiryForm()
      ) {

        return;

      }


      // ----------------------------------------------------------
      // IMPORTANT
      // selectedRows contains table indexes.
      //
      // Example:
      // selectedRows = [0, 2]
      //
      // data[0].id = 29287
      // data[2].id = 29291
      //
      // Convert them into:
      //
      // [
      //   { id: 29287 },
      //   { id: 29291 }
      // ]
      // ----------------------------------------------------------

      const idSelected =
        selectedRows

          .map(
            (rowIndex) => {

              const selectedRow =
                data[rowIndex];


              if (
                !selectedRow ||
                !selectedRow.id
              ) {

                return null;

              }


              return {

                id: Number(
                  selectedRow.id
                )

              };

            }
          )

          .filter(Boolean);


      if (
        idSelected.length === 0
      ) {

        setSnackbarSeverity(
          "error"
        );

        setSnackbarMessage(
          "Please select at least one valid enquiry"
        );

        setSnackbarOpen(
          true
        );

        return;

      }


      startLoading();


      try {

        const config = {

          headers: {

            "Content-Type":
              "application/json",

            Accept:
              "application/json",

            Authorization:
              `${token}`,

            adminId:
              `${adminId}`,

            userLevel:
              `${userLevel}`

          },

          timeout: Infinity

        };


        // ========================================================
        // EXACT MOVE API PAYLOAD
        // ========================================================

        const payload = {

          move_type:
            Number(moveType),

          client_name:
            clientName,

          state_id:
            selectedState?.id
              ? Number(
                  selectedState.id
                )
              : null,

          start_date:
            fromDate,

          end_date:
            toDate,

          id_selected:
            idSelected

        };


        console.log(
          "=============================="
        );

        console.log(
          "MOVE FINANCE PAYLOAD"
        );

        console.log(
          JSON.stringify(
            payload,
            null,
            2
          )
        );

        console.log(
          "=============================="
        );


        const response =
          await axios.post(

            `${API_BASE_URL}/api/admin/v2/move-finance-enquiries`,

            payload,

            config

          );


        console.log(
          "Move Finance Response:",
          response.data
        );


        // ========================================================
        // SUCCESS
        // ========================================================

        if (
          response?.data
            ?.status_code ===
            200 &&
          response?.data
            ?.error === false
        ) {

          setSnackbarSeverity(
            "success"
          );


          let message =
            response?.data
              ?.message;


          if (
            Array.isArray(
              message
            )
          ) {

            message =
              message.join(
                ", "
              );

          }


          // If backend returns counts
          const resultData =
            response?.data
              ?.data;


          if (
            resultData &&
            typeof resultData ===
              "object"
          ) {

            const resultMessage =
              [
                `Total: ${
                  resultData.total ??
                  0
                }`,

                `Moved: ${
                  resultData.moved ??
                  0
                }`,

                `Valid: ${
                  resultData.valid ??
                  0
                }`,

                `Invalid: ${
                  resultData.invalid ??
                  0
                }`,

                `Duplicate: ${
                  resultData.duplicate ??
                  0
                }`,

                `Already Moved: ${
                  resultData
                    .already_moved ??
                  0
                }`

              ].join(" | ");


            setSnackbarMessage(
              resultMessage
            );

          } else {

            setSnackbarMessage(

              message ||
              "Finance enquiries processed successfully"

            );

          }


          setSnackbarOpen(
            true
          );


          // Clear selected rows
          setSelectedRows([]);


          // Close confirmation
          setOpenConfirmModal(
            false
          );


          // Refresh listing
          await fetchData();


        } else {

          // ======================================================
          // BACKEND RETURNED ERROR
          // ======================================================

          setSnackbarSeverity(
            "error"
          );


          let message =
            response?.data
              ?.message;


          if (
            Array.isArray(
              message
            )
          ) {

            message =
              message.join(
                ", "
              );

          }


          setSnackbarMessage(

            message ||
            "Unable to move finance enquiries"

          );


          setSnackbarOpen(
            true
          );

        }


      } catch (error) {

        console.error(
          "Move Finance Error:",
          error
        );


        console.error(
          "Move Finance Backend Error:",
          error?.response?.data
        );


        setSnackbarSeverity(
          "error"
        );


        let message =
          error?.response
            ?.data?.message;


        // Laravel failFunction may return
        // messages as array.
        if (
          Array.isArray(
            message
          )
        ) {

          message =
            message.join(
              ", "
            );

        }


        if (
          !message &&
          Array.isArray(
            error?.response
              ?.data?.data
          )
        ) {

          message =
            error.response
              .data.data
              .join(", ");

        }


        setSnackbarMessage(

          message ||
          error?.message ||
          "Something went wrong while moving finance enquiries"

        );


        setSnackbarOpen(
          true
        );


      } finally {

        stopLoading();

      }

    };


  // ============================================================
  // CONFIRM
  // ============================================================

  const handleConfirmSubmit =
    async () => {

      await handleSubmits();

    };


  // ============================================================
  // VIEW
  // ============================================================

  const handleViewRow = (
    rowData
  ) => {

    setViewRowData(
      rowData
    );

    setOpenViewModal(
      true
    );

  };


  // ============================================================
  // SNACKBAR
  // ============================================================

  const handleCloseSnackbar = (
    event,
    reason
  ) => {

    if (
      reason ===
      "clickaway"
    ) {

      return;

    }


    setSnackbarOpen(
      false
    );

  };


  // ============================================================
  // TABLE COLUMNS
  // ============================================================

  const columns = [

    // CHECKBOX
    {
      name: "id",

      label: (

        <input

          type="checkbox"

          checked={
            data.length > 0 &&
            selectedRows.length ===
              data.length
          }

          onChange={
            handleSelectAllCheck
          }

        />

      ),

      options: {

        filter:
          false,

        sort:
          false,

        customBodyRender: (
          value,
          tableMeta
        ) => (

          <input

            type="checkbox"

            checked={
              selectedRows.includes(
                tableMeta.rowIndex
              )
            }

            onClick={(e) =>
              e.stopPropagation()
            }

            onChange={() =>
              handleRowSelect(
                tableMeta.rowIndex
              )
            }

          />

        )

      }

    },


    {
      name:
        "serial",

      label:
        "S/N",

      options: {

        filter:
          false,

        sort:
          false

      }

    },


    {
      name:
        "enquiry_name",

      label:
        "Enquiry Name"
    },


    {
      name:
        "enquiry_type",

      label:
        "Enquiry Type"
    },


    {
      name:
        "product_code",

      label:
        "Product Code"
    },


    {
      name:
        "user",

      label:
        "Customer Name"
    },


    {
      name:
        "mobile",

      label:
        "Mobile"
    },


    {
      name:
        "email",

      label:
        "Email"
    },


    {
      name:
        "state",

      label:
        "State"
    },


    {
      name:
        "city",

      label:
        "City"
    },


    {
      name:
        "taluka",

      label:
        "Taluka"
    },


    {
      name:
        "created_at",

      label:
        "Created At"
    },


    {
      name:
        "action",

      label:
        "Action",

      options: {

        filter:
          false,

        sort:
          false,

        customBodyRenderLite:
          (dataIndex) => {

            const rowData =
              data[dataIndex];


            return (

              <IconButton

                size="small"

                onClick={(e) => {

                  e.stopPropagation();

                  handleViewRow(
                    rowData
                  );

                }}

                style={{

                  color:
                    "#1976d2",

                  background:
                    "#e3f2fd"

                }}

              >

                <VisibilityIcon />

              </IconButton>

            );

          }

      }

    }

  ];


  // ============================================================
  // TABLE OPTIONS
  // ============================================================

  const options = {

    rowsPerPage:
      perPage,

    selectableRows:
      "none",

    page:
      page,

    serverSide:
      true,

    count:
      totalCount,

    filter:
      false,

    search:
      false,

    download:
      false,

    print:
      false,

    viewColumns:
      false,

    responsive:
      "standard",

    fixedHeader:
      true,


    rowsPerPageOptions: [
      10,
      50,
      100,
      300,
      500,
      1000
    ],


    onTableChange: (
      action,
      tableState
    ) => {

      switch (action) {

        case "changePage":

          setPage(
            tableState.page
          );

          setSelectedRows([]);

          break;


        case "changeRowsPerPage":

          setPerPage(
            tableState.rowsPerPage
          );

          setPage(0);

          setSelectedRows([]);

          break;


        default:

          break;

      }

    },


    setRowProps: (
      row,
      dataIndex
    ) => ({

      style: {

        backgroundColor:
          selectedRows.includes(
            dataIndex
          )
            ? "#D2DE32"
            : "inherit",

        cursor:
          "pointer"

      },

      onClick: () =>

        handleRowSelect(
          dataIndex
        )

    })

  };


  // ============================================================
  // UI
  // ============================================================

  return (

    <>

      {/* Snackbar */}

      <Snackbar

        anchorOrigin={{

          vertical:
            "top",

          horizontal:
            "right"

        }}

        open={
          snackbarOpen
        }

        autoHideDuration={
          6000
        }

        onClose={
          handleCloseSnackbar
        }

      >

        <MuiAlert

          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbarSeverity
          }

          variant="filled"

          sx={{
            width:
              "100%"
          }}

        >

          {
            snackbarMessage
          }

        </MuiAlert>

      </Snackbar>


      {/* TITLE */}

      <Box
        sx={{
          width:
            "100%"
        }}
      >

        <Grid container>

          <Grid
            item
            xs={12}
            style={{
              marginTop:
                "8px"
            }}
          >

            <Typography
              className={
                classNames(
                  classes.pagetittle,
                  classes.pagetittlestyle
                )
              }
            >

              Finance Enquiries

            </Typography>

          </Grid>

        </Grid>


        <hr
          className={
            classes.horizontalline
          }
        />

      </Box>


      {/* FILTERS */}

      <div
        className={
          classes.root
        }
      >

        <Grid
          container
          spacing={1}
          alignItems="center"
        >


          {/* START DATE */}

          <Grid
            item
            xs={6}
            sm={3}
            lg={2}
          >

            <input

              type="date"

              name="fromDate"

              value={
                fromDate
              }

              onChange={
                handleDateChange
              }

              className={
                classes.inputform
              }

            />

          </Grid>


          {/* END DATE */}

          <Grid
            item
            xs={6}
            sm={3}
            lg={2}
          >

            <input

              type="date"

              name="toDate"

              value={
                toDate
              }

              onChange={
                handleDateChange
              }

              className={
                classes.inputform
              }

            />

          </Grid>


          {/* STATE */}

          <Grid
            item
            xs={6}
            sm={3}
            lg={2}
          >

            <Autocomplete

              options={
                stateOptions
              }

              getOptionLabel={
                (option) =>
                  option?.title ||
                  ""
              }

              renderInput={
                (params) => (

                  <TextField
                    {...params}

                    placeholder="Select State"

                    variant="outlined"
                  />

                )
              }

              value={
                selectedState
              }

              onChange={
                handleStateChange
              }

              getOptionSelected={(
                option,
                value
              ) =>
                option.id ===
                value.id
              }

            />

          </Grid>


          {/* ENQUIRY TYPE */}

          <Grid
            item
            xs={6}
            sm={3}
            lg={2}
          >

            <select

              className={
                classes.selectform
              }

              value={
                enquiryType
              }

              onChange={(e) => {

                setEnquiryType(
                  e.target.value
                );

                setIsDataLoaded(
                  false
                );

                setSelectedRows([]);

              }}

            >

              <option value="">
                Select Enquiry Type
              </option>

              <option value="New Tractor">
                New Tractor
              </option>

              <option value="Personal Loan">
                Personal Loan
              </option>

              <option value="Earth Mover">
                Earth Mover
              </option>

              <option value="Old Tractor">
                Old Tractor
              </option>

            </select>

          </Grid>


          {/* CLIENT */}

          <Grid
            item
            xs={6}
            sm={3}
            lg={2}
          >

            <select

              className={
                classes.selectform
              }

              value={
                clientName
              }

              onChange={(e) => {

                setClientName(
                  e.target.value
                );

                setIsDataLoaded(
                  false
                );

              }}

            >

              <option value="">
                Select Client
              </option>

              <option value="Tyger">
                Tyger
              </option>

            </select>

          </Grid>


          {/* MOVE TYPE */}

          <Grid
            item
            xs={6}
            sm={3}
            lg={2}
          >

            <select

              className={
                classes.selectform
              }

              value={
                moveType
              }

              onChange={(e) => {

                setMoveType(
                  e.target.value
                );

                setIsDataLoaded(
                  false
                );

              }}

            >

              <option value="">
                Select Move Type
              </option>

              <option value="1">
                Move To Client API
              </option>

            </select>

          </Grid>


          {/* BUTTONS */}

          <Grid
            item
            xs={12}
          >

            <div
              style={{

                display:
                  "flex",

                alignItems:
                  "center",

                gap:
                  "10px",

                flexWrap:
                  "wrap",

                marginTop:
                  "10px"

              }}
            >


              <Button

                onClick={
                  handleResetFields
                }

                variant="outlined"

                startIcon={
                  <ClearIcon />
                }

                style={{

                  backgroundColor:
                    "#dc3545",

                  borderColor:
                    "#dc3545",

                  color:
                    "#fff"

                }}

                className={
                  classes.buttonform
                }

              >

                Reset Fields

              </Button>


              <Button

                onClick={
                  handleDataLoaded
                }

                variant="contained"

                startIcon={

                  isLoading ? (

                    <CircularProgress
                      size={18}
                    />

                  ) : isDataLoaded ? (

                    <CheckCircleIcon />

                  ) : (

                    <CloudDownloadIcon />

                  )

                }

                disabled={
                  isLoading
                }

                style={{

                  backgroundColor:
                    "#1976d2",

                  color:
                    "#fff"

                }}

                className={
                  classes.buttonform
                }

              >

                {
                  isLoading
                    ? "Loading..."
                    : isDataLoaded
                      ? "Data Loaded"
                      : "Get Data"
                }

              </Button>


              <Button

                disabled={

                  isLoading ||

                  !isDataLoaded ||

                  selectedRows.length ===
                    0

                }

                onClick={
                  handleMoveEnquiryClick
                }

                style={{

                  backgroundColor:
                    "#1976d2",

                  color:
                    "#fff"

                }}

                className={
                  classes.buttonform
                }

                variant="outlined"

                startIcon={
                  <AssignmentIndIcon />
                }

              >

                {
                  selectedRows.length
                } - Move Enquiry

              </Button>

            </div>

          </Grid>

        </Grid>

      </div>


      <br />


      {/* TABLE */}

      <MuiThemeProvider
        theme={
          customTheme()
        }
      >

        {
          isLoading &&
          <LinearProgress />
        }


        <MUIDataTable

          title={

            isLoading ? (

              <Skeleton
                animation="wave"
              />

            ) : (

              <Typography
                className={
                  classes.tabletittle
                }
              >

                {
                  selectedRows.length >
                    0 &&
                  `Selected Records : ${selectedRows.length}`
                }

              </Typography>

            )

          }

          data={
            data
          }

          options={
            options
          }

          columns={
            columns
          }

        />

      </MuiThemeProvider>


      {/* CONFIRM MOVE MODAL */}

      <Modal

        open={
          openConfirmModal
        }

        onClose={() =>
          setOpenConfirmModal(
            false
          )
        }

      >

        <Box
          sx={{

            position:
              "absolute",

            top:
              "50%",

            left:
              "50%",

            transform:
              "translate(-50%, -50%)",

            bgcolor:
              "background.paper",

            p: 3,

            borderRadius:
              2,

            minWidth:
              450,

            textAlign:
              "center",

            boxShadow:
              24

          }}
        >

          <Typography
            variant="h6"
            gutterBottom
          >

            Move Finance Enquiries

          </Typography>


          <Typography
            style={{
              marginBottom:
                "10px"
            }}
          >

            Are you sure you want to move{" "}

            <b>
              {
                selectedRows.length
              }
            </b>{" "}

            selected finance enquiries?

          </Typography>


          <Typography
            variant="body2"
            style={{
              marginBottom:
                "20px"
            }}
          >

            Client:{" "}

            <b>
              {clientName}
            </b>

            {" | "}

            State:{" "}

            <b>
              {
                selectedState
                  ?.title
              }
            </b>

          </Typography>


          <div
            style={{

              display:
                "flex",

              justifyContent:
                "center",

              gap:
                "10px"

            }}
          >

            <Button

              variant="contained"

              color="primary"

              onClick={
                handleConfirmSubmit
              }

              disabled={
                isLoading
              }

            >

              {
                isLoading
                  ? "Processing..."
                  : "Yes, Move Enquiry"
              }

            </Button>


            <Button

              variant="outlined"

              color="secondary"

              onClick={() =>
                setOpenConfirmModal(
                  false
                )
              }

            >

              Cancel

            </Button>

          </div>

        </Box>

      </Modal>


      {/* VIEW MODAL */}

      <Modal

        open={
          openViewModal
        }

        onClose={() =>
          setOpenViewModal(
            false
          )
        }

      >

        <Box
          sx={{

            position:
              "absolute",

            top:
              "50%",

            left:
              "50%",

            transform:
              "translate(-50%, -50%)",

            bgcolor:
              "#fff",

            p:
              3,

            borderRadius:
              2,

            width: {
              xs:
                "90%",
              md:
                700
            },

            maxHeight:
              "90vh",

            overflowY:
              "auto",

            boxShadow:
              24

          }}
        >

          <div
            style={{

              display:
                "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",

              marginBottom:
                "20px"

            }}
          >

            <Typography
              variant="h6"
            >

              Finance Enquiry Details

            </Typography>


            <IconButton

              onClick={() =>
                setOpenViewModal(
                  false
                )
              }

              size="small"

            >

              <CloseIcon />

            </IconButton>

          </div>


          {
            viewRowData && (

              <Grid
                container
                spacing={2}
              >

                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Enquiry Name:
                  </b>{" "}

                  {
                    viewRowData
                      .enquiry_name
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Enquiry Type:
                  </b>{" "}

                  {
                    viewRowData
                      .enquiry_type
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Product Code:
                  </b>{" "}

                  {
                    viewRowData
                      .product_code
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Customer:
                  </b>{" "}

                  {
                    viewRowData.user
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Mobile:
                  </b>{" "}

                  {
                    viewRowData.mobile
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Email:
                  </b>{" "}

                  {
                    viewRowData.email ||
                    "-"
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    State:
                  </b>{" "}

                  {
                    viewRowData.state
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    City:
                  </b>{" "}

                  {
                    viewRowData.city
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Taluka:
                  </b>{" "}

                  {
                    viewRowData.taluka
                  }

                </Grid>


                <Grid
                  item
                  xs={6}
                >

                  <b>
                    Date:
                  </b>{" "}

                  {
                    viewRowData
                      .created_at
                  }

                </Grid>


                <Grid
                  item
                  xs={12}
                >

                  <b>
                    Response:
                  </b>


                  <pre
                    style={{

                      marginTop:
                        "10px",

                      whiteSpace:
                        "pre-wrap",

                      wordBreak:
                        "break-word",

                      background:
                        "#f5f5f5",

                      padding:
                        "10px",

                      borderRadius:
                        "4px"

                    }}
                  >

                    {
                      viewRowData
                        .response_data ||
                      "-"
                    }

                  </pre>

                </Grid>

              </Grid>

            )
          }

        </Box>

      </Modal>

    </>

  );

};


export default FinanceEnquiries;